import { SxProps, Theme } from '@mui/material';

// Typography styles
export const timeTextStyle: SxProps<Theme> = {
  minWidth: 60,
  fontFamily: 'Sohne, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '16px',
  letterSpacing: '0.15px'
};

export const descriptionTextStyle: SxProps<Theme> = {
  flex: 1,
  fontSize: '14px',
  lineHeight: 1.3
};

export const scoreTextStyle: SxProps<Theme> = {
  fontSize: '32px',
  fontWeight: 500,
  lineHeight: '32px',
  fontFamily: 'Sohne, sans-serif',
  letterSpacing: '0px',
};

// Inline running-score cluster shown on the right of a scoring event row
// (NBA baskets / made free throws). Distinct from scoreTextStyle, which is the
// large centered scoreboard used by the full-width GoalEventItem block.
export const inlineScoreContainerStyle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  justifyContent: 'flex-end',
};

export const inlineScoreTextStyle: SxProps<Theme> = {
  fontFamily: 'Sohne, sans-serif',
  fontSize: '14px',
  lineHeight: '16px',
  letterSpacing: '0.15px',
  // Keep both numerals the same advance width so the pair doesn't shift as the
  // score climbs into double/triple digits.
  fontVariantNumeric: 'tabular-nums',
};

// A row that just arrived live slides/fades in. NEW_EVENT_FLASH_MS is how long
// it stays flagged as new (PlayByPlayPanel clears the set after it).
export const NEW_EVENT_FLASH_MS = 2000;
const NEW_EVENT_ENTER_MS = 350;

export const newEventFlashStyle: SxProps<Theme> = {
  '@keyframes pbpNewEventEnter': {
    from: { opacity: 0, transform: 'translateY(12px)' },
  },
  animation: `pbpNewEventEnter ${NEW_EVENT_ENTER_MS}ms cubic-bezier(0.2, 0, 0, 1)`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};

// Rows past the stream's content end render dimmed and inert — there's no
// video to seek to or clip yet — then fade up and become clickable once it
// exists.
export const playheadOpacityStyle = (isAhead: boolean) => ({
  opacity: isAhead ? 0.5 : 1,
  transition: 'opacity 400ms ease-out',
  pointerEvents: isAhead ? ('none' as const) : undefined,
});

// Container styles
export const regularEventContainerStyle = (
  bgColor: string,
  isNew?: boolean,
  isAhead?: boolean,
): SxProps<Theme> => ({
  py: 0.5,
  px: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  bgcolor: bgColor,
  borderBottom: '1px solid rgba(255,255,255,0.05)',
  '&:hover': {
    bgcolor: 'rgba(255,255,255,0.08)',
    cursor: 'pointer'
  },
  ...(isNew && newEventFlashStyle),
  ...(isAhead !== undefined && playheadOpacityStyle(isAhead)),
});


export const goalEventRowStyle = (teamColor: string): SxProps<Theme> => ({
  py: 0.5,
  px: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  bgcolor: teamColor,
  borderTop: '1px solid rgba(0,0,0,0.2)',
  '&:hover': {
    cursor: 'pointer'
  },
})

export const scoreDisplayStyle: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 3,
  py: 2,
  borderBottom: '1px solid white',
};

// Logo/Avatar styles
export const teamLogoStyle: SxProps<Theme> = {
  width: 32,
  height: 32,
  objectFit: 'contain',
};

export const teamAvatarStyle = (color: string): SxProps<Theme> => ({
  width: 32,
  height: 32,
  bgcolor: color || 'rgba(255,255,255,0.1)',
});

export const scoreBoardLogoStyle: SxProps<Theme> = {
  width: 64,
  height: 64,
  objectFit: 'contain',
};

export const scoreBoardAvatarStyle = (color: string): SxProps<Theme> => ({
  width: 64,
  height: 64,
  bgcolor: color,
});

export const emptyPlaceholderStyle: SxProps<Theme> = {
  width: 32,
  height: 32,
};
